import { FC, PointerEvent as ReactPointerEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { BasicButton } from 'src/components/widgets/buttons';
import { Drawer } from 'src/components/widgets/drawer';
import { Icon } from 'src/components/widgets/icon';
import { InlinePanelLayout } from 'src/components/widgets/inlineHelpLayout';
import { useRelayStore } from 'src/relay/environment';
import { T } from 'src/shared/helpers';
import DashboardIcon from 'src/shared/material_icons/action/dashboard.svg';
import SmartToyIcon from 'src/shared/material_icons/hardware/smart_toy.svg';
import styled from 'styled-components';
import { AgentAssistant } from './AgentAssistant';
import { useAgentAssistant } from './AgentAssistantContext';

const AI_MODE_EXIT_DURATION_MS = 220;
const LAUNCHER_SIZE_PX = 52;
const LAUNCHER_VIEWPORT_MARGIN_PX = 12;
const LAUNCHER_POSITION_STORAGE_KEY = 'zakeke-agent-assistant-launcher-position';

interface LauncherPosition {
    x: number;
    y: number;
}

interface LauncherDragState {
    pointerId: number;
    startPointerX: number;
    startPointerY: number;
    startLauncherX: number;
    startLauncherY: number;
    moved: boolean;
}

const clampLauncherPosition = (position: LauncherPosition): LauncherPosition => ({
    x: Math.min(
        Math.max(position.x, LAUNCHER_VIEWPORT_MARGIN_PX),
        Math.max(LAUNCHER_VIEWPORT_MARGIN_PX, window.innerWidth - LAUNCHER_SIZE_PX - LAUNCHER_VIEWPORT_MARGIN_PX)
    ),
    y: Math.min(
        Math.max(position.y, LAUNCHER_VIEWPORT_MARGIN_PX),
        Math.max(LAUNCHER_VIEWPORT_MARGIN_PX, window.innerHeight - LAUNCHER_SIZE_PX - LAUNCHER_VIEWPORT_MARGIN_PX)
    )
});

const readLauncherPosition = (): LauncherPosition | null => {
    if (typeof window === 'undefined') return null;

    try {
        const storedPosition = window.localStorage.getItem(LAUNCHER_POSITION_STORAGE_KEY);
        if (!storedPosition) return null;

        const position = JSON.parse(storedPosition) as Partial<LauncherPosition>;
        if (typeof position.x !== 'number' || typeof position.y !== 'number' || !Number.isFinite(position.x) || !Number.isFinite(position.y)) return null;

        return clampLauncherPosition({ x: position.x, y: position.y });
    } catch {
        return null;
    }
};

const saveLauncherPosition = (position: LauncherPosition) => {
    try {
        window.localStorage.setItem(LAUNCHER_POSITION_STORAGE_KEY, JSON.stringify(position));
    } catch {
        // The launcher remains draggable when browser storage is unavailable.
    }
};

const Launcher = styled.button<{ $position: LauncherPosition | null; $isDragging: boolean }>`
    position: fixed;
    top: ${(props) => (props.$position ? `${props.$position.y}px` : 'auto')};
    right: ${(props) => (props.$position ? 'auto' : '24px')};
    bottom: ${(props) => (props.$position ? 'auto' : '24px')};
    left: ${(props) => (props.$position ? `${props.$position.x}px` : 'auto')};
    z-index: 20;
    display: flex;
    width: 52px;
    height: 52px;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 50%;
    background: linear-gradient(135deg, #00a1b5, #007c91);
    box-shadow: 0 8px 24px rgba(0, 124, 145, 0.32);
    color: white;
    cursor: ${(props) => (props.$isDragging ? 'grabbing' : 'grab')};
    touch-action: none;
    user-select: none;
    transition: ${(props) => (props.$isDragging ? 'none' : 'box-shadow 0.15s ease')};

    &:hover {
        box-shadow: 0 10px 28px rgba(0, 124, 145, 0.4);
    }

    ${Icon},
    ${Icon} svg {
        width: 24px;
        height: 24px;
    }
`;

const ClassicModeButton = styled(BasicButton)`
    border-color: rgba(255, 255, 255, 0.72);
    background: rgba(255, 255, 255, 0.94);
    color: #007c91;
    white-space: nowrap;

    &:hover {
        border-color: white;
        background: white;
    }
`;

const AiModeHeader = styled.div`
    display: flex;
    min-height: 76px;
    align-items: center;
    gap: 14px;
    padding: 14px 220px 14px 28px;
    background: radial-gradient(circle at 12% 20%, rgba(255, 255, 255, 0.2), transparent 28%), linear-gradient(120deg, #004d5a 0%, #007c91 48%, #00a1b5 100%);
    color: white;
`;

const AiModeIdentity = styled.div`
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 12px;
`;

const AiModeIcon = styled.div`
    display: flex;
    width: 42px;
    height: 42px;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.16);
    backdrop-filter: blur(8px);

    ${Icon},
    ${Icon} svg {
        width: 23px;
        height: 23px;
    }
`;

const AiModeHeading = styled.div`
    min-width: 0;

    strong {
        display: block;
        font-size: 16px;
        line-height: 1.25;
    }

    span {
        display: block;
        margin-top: 3px;
        overflow: hidden;
        color: rgba(255, 255, 255, 0.82);
        font-size: 12px;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const AiModeBadge = styled.span`
    flex-shrink: 0;
    padding: 5px 9px;
    border: 1px solid rgba(255, 255, 255, 0.38);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.14);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
`;

const AgentSurface = styled.div<{ $aiMode: boolean }>`
    display: flex;
    flex: 1;
    min-width: 0;
    min-height: 0;
    padding: ${(props) => (props.$aiMode ? '24px' : '0')};
    background: ${(props) =>
        props.$aiMode
            ? `
                radial-gradient(circle at 8% 12%, rgba(0, 161, 181, 0.1), transparent 27%),
                radial-gradient(circle at 92% 88%, rgba(0, 124, 145, 0.08), transparent 30%),
                #f2f8f9
            `
            : 'white'};
`;

const AgentWorkspace = styled.div<{ $aiMode: boolean }>`
    display: flex;
    width: 100%;
    max-width: ${(props) => (props.$aiMode ? '1600px' : 'none')};
    min-width: 0;
    min-height: 0;
    margin: 0 auto;
    overflow: hidden;
    border: ${(props) => (props.$aiMode ? '1px solid rgba(0, 124, 145, 0.16)' : '0')};
    border-radius: ${(props) => (props.$aiMode ? '16px' : '0')};
    background: white;
    box-shadow: ${(props) => (props.$aiMode ? '0 18px 50px rgba(0, 77, 90, 0.12)' : 'none')};
    container-type: inline-size;
`;

export interface AgentAssistantLauncherProps {
    /** Starts a new, empty conversation with this message. Restored conversations are never changed. */
    initialPrompt?: string;
    children: ReactNode;
    disabled?: boolean;
}

export const AgentAssistantLauncher: FC<AgentAssistantLauncherProps> = ({ initialPrompt, children, disabled = false }) => {
    const { isEnabled, isOpen, isFullScreen, animateFullScreenEntry, openSidebar, openClassicMode, close, changeFullScreen } = useAgentAssistant();
    const [isExitingAiMode, setIsExitingAiMode] = useState(false);
    const [launcherPosition, setLauncherPosition] = useState<LauncherPosition | null>(() => readLauncherPosition());
    const [isDraggingLauncher, setIsDraggingLauncher] = useState(false);
    const launcherPositionRef = useRef<LauncherPosition | null>(launcherPosition);
    const launcherDragState = useRef<LauncherDragState | null>(null);
    const suppressLauncherClick = useRef(false);
    const label = T._('Personal AI assistant', 'Admin');
    const invalidateRelayStore = useRelayStore((state) => state.invalidateRelayStore);

    const refreshRelayStoreIfNeeded = useCallback(() => {
        const relayStore = useRelayStore.getState();
        if (relayStore.isRelayStoreDirty) relayStore.incrementRelayStoreKey();
    }, []);

    const closeAndRefresh = useCallback(() => {
        refreshRelayStoreIfNeeded();
        close();
    }, [close, refreshRelayStoreIfNeeded]);

    useEffect(() => {
        if (!isExitingAiMode) return;

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const timer = window.setTimeout(
            () => {
                setIsExitingAiMode(false);
                refreshRelayStoreIfNeeded();
                openClassicMode();
            },
            prefersReducedMotion ? 0 : AI_MODE_EXIT_DURATION_MS
        );

        return () => window.clearTimeout(timer);
    }, [isExitingAiMode, openClassicMode, refreshRelayStoreIfNeeded]);

    useEffect(() => {
        const keepLauncherInViewport = () => {
            const position = launcherPositionRef.current;
            if (!position) return;

            const clampedPosition = clampLauncherPosition(position);
            launcherPositionRef.current = clampedPosition;
            setLauncherPosition(clampedPosition);
            saveLauncherPosition(clampedPosition);
        };

        window.addEventListener('resize', keepLauncherInViewport);
        return () => window.removeEventListener('resize', keepLauncherInViewport);
    }, []);

    const handleLauncherPointerDown = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => {
        if (event.button !== 0) return;

        const bounds = event.currentTarget.getBoundingClientRect();
        launcherDragState.current = {
            pointerId: event.pointerId,
            startPointerX: event.clientX,
            startPointerY: event.clientY,
            startLauncherX: bounds.left,
            startLauncherY: bounds.top,
            moved: false
        };
        event.currentTarget.setPointerCapture(event.pointerId);
    }, []);

    const handleLauncherPointerMove = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => {
        const dragState = launcherDragState.current;
        if (!dragState || dragState.pointerId !== event.pointerId) return;

        const deltaX = event.clientX - dragState.startPointerX;
        const deltaY = event.clientY - dragState.startPointerY;
        if (!dragState.moved && Math.hypot(deltaX, deltaY) < 4) return;

        dragState.moved = true;
        setIsDraggingLauncher(true);
        const position = clampLauncherPosition({
            x: dragState.startLauncherX + deltaX,
            y: dragState.startLauncherY + deltaY
        });
        launcherPositionRef.current = position;
        setLauncherPosition(position);
    }, []);

    const finishLauncherDrag = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => {
        const dragState = launcherDragState.current;
        if (!dragState || dragState.pointerId !== event.pointerId) return;

        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
        launcherDragState.current = null;
        setIsDraggingLauncher(false);

        if (!dragState.moved) return;

        suppressLauncherClick.current = event.type === 'pointerup';
        if (launcherPositionRef.current) saveLauncherPosition(launcherPositionRef.current);
    }, []);

    const handleLauncherClick = useCallback(() => {
        if (suppressLauncherClick.current) {
            suppressLauncherClick.current = false;
            return;
        }

        openSidebar();
    }, [openSidebar]);

    if (!isEnabled || disabled) return <>{children}</>;

    return (
        <>
            <InlinePanelLayout
                isOpen={isOpen && !isFullScreen}
                onClose={closeAndRefresh}
                panelTitle={label}
                panelWidth={620}
                panelContent={
                    isOpen && !isFullScreen ? (
                        <AgentWorkspace $aiMode={false}>
                            <AgentAssistant initialPrompt={initialPrompt} onExternalChanges={invalidateRelayStore} />
                        </AgentWorkspace>
                    ) : null
                }
            >
                {children}
            </InlinePanelLayout>
            {!isOpen && (
                <Launcher
                    type='button'
                    aria-label={label}
                    $position={launcherPosition}
                    $isDragging={isDraggingLauncher}
                    onClick={handleLauncherClick}
                    onPointerDown={handleLauncherPointerDown}
                    onPointerMove={handleLauncherPointerMove}
                    onPointerUp={finishLauncherDrag}
                    onPointerCancel={finishLauncherDrag}
                >
                    <Icon>
                        <SmartToyIcon />
                    </Icon>
                </Launcher>
            )}
            <Drawer
                isOpen={isOpen && isFullScreen}
                onClose={closeAndRefresh}
                header={{ title: label, subtitle: T._('Get guided help with your Zakeke tasks.', 'Admin') }}
                fullScreenHeader={
                    <AiModeHeader>
                        <AiModeIdentity>
                            <AiModeIcon>
                                <Icon>
                                    <SmartToyIcon />
                                </Icon>
                            </AiModeIcon>
                            <AiModeHeading>
                                <strong>{label}</strong>
                                <span>{T._('A dedicated workspace for your AI-assisted tasks.', 'Admin')}</span>
                            </AiModeHeading>
                        </AiModeIdentity>
                        <AiModeBadge>{T._('AI mode', 'Admin')}</AiModeBadge>
                    </AiModeHeader>
                }
                headerActions={
                    isFullScreen ? (
                        <ClassicModeButton type='button' $size='small' disabled={isExitingAiMode} onClick={() => setIsExitingAiMode(true)}>
                            <Icon>
                                <DashboardIcon />
                            </Icon>
                            {T._('Back to Classic mode', 'Admin')}
                        </ClassicModeButton>
                    ) : undefined
                }
                width={620}
                showFullScreenButton={!isFullScreen}
                showCloseButton={!isFullScreen}
                isFullScreen={isFullScreen}
                onFullScreenChange={changeFullScreen}
                fullScreenTransition='fade'
                animateFullScreenEntry={animateFullScreenEntry}
                isFullScreenExiting={isExitingAiMode}
            >
                {({ isFullScreen: fullScreen }) => (
                    <AgentSurface $aiMode={fullScreen}>
                        <AgentWorkspace $aiMode={fullScreen}>
                            <AgentAssistant initialPrompt={initialPrompt} onExternalChanges={invalidateRelayStore} />
                        </AgentWorkspace>
                    </AgentSurface>
                )}
            </Drawer>
        </>
    );
};
